import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Box, ButtonList, DropdownButton, DropdownButtonContext, FlexBox, InputText, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useSearchFilters } from '../../searchFilters.context';
import { filterFieldAssets } from '../../utils/definitions';
import styles from './filterAddCondition.module.scss';
/** Represents a component that can be used to add multiple filters in search */
export const FilterAddCondition = ({ primary = false, tooltips = true, children, }) => {
    const buttonRef = useRef(null);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const { tab, fields, addCondition } = useSearchFilters();
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    /** Clears the search input after closing the dropdown. */
    useEffect(() => {
        if (!open) {
            setSearch('');
        }
    }, [open]);
    /**
     * A callback ref used to autofocus the search input when opening the
     * dropdown.
     */
    const inputRef = useCallback((inputElement) => {
        if (open && inputElement && !isSmallScreen) {
            inputElement.focus();
        }
    }, [isSmallScreen, open]);
    /** Adds a condition with the selected field and clears the search input. */
    const handleClick = (field) => {
        addCondition(field);
        setSearch('');
        /**
         * When there are many conditions already added, we scroll the button into
         * view, so the next condition can be easily added without the user having
         * to scroll manually.
         */
        setTimeout(() => {
            var _a;
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth' });
        });
    };
    /** The filter fields that can be filtered on the current tab. */
    const fieldsToDisplay = useMemo(() => {
        if (!search)
            return fields;
        return fields.filter((field) => filterFieldAssets[field].label
            .toLowerCase()
            .includes(search.toLowerCase()));
    }, [fields, search]);
    /** The search input is only necessary when there are a lot fields. */
    const isSearchable = fields.length > 4;
    return (_jsx(DropdownButton, Object.assign({ className: styles.button, icon: "plus", buttonProps: {
            variant: primary ? 'primary' : 'tertiary',
            innerRef: buttonRef,
        }, dropdownProps: {
            className: styles.dropdown,
        }, fixed: true, noArrow: true, onToggle: setOpen, dropdown: _jsx(DropdownButtonContext.Consumer, { children: ({ toggleDropdown }) => (_jsxs(FlexBox, Object.assign({ className: styles.dropdownInner, flexDirection: "column" }, { children: [isSearchable && (_jsx(Box, Object.assign({ padding: "300", className: styles.dropdownTop }, { children: _jsx(InputText, { value: search, onChange: (e) => setSearch(e.target.value), placeholder: "Search for a filter type...", icon: "search", ref: inputRef }) }))), _jsx(ButtonList, Object.assign({ className: styles.dropdownContent }, { children: fieldsToDisplay.map((field) => {
                            const { label, description, icon, isNew = false, } = filterFieldAssets[field];
                            return (_jsx(Tooltip, Object.assign({ className: styles.tooltip, head: label, content: description[tab], placement: "left", disabled: !tooltips }, { children: _jsx(ButtonList.Item, Object.assign({ className: styles.buttonListItem, icon: icon, onClick: () => {
                                        handleClick(field);
                                        toggleDropdown();
                                    } }, { children: _jsxs(FlexBox, Object.assign({ alignItems: "center", gap: "200", as: "span" }, { children: [_jsx("span", { children: label }), isNew && (_jsx(Badge, Object.assign({ size: "small", variant: "success" }, { children: "New" })))] })) })) }), field));
                        }) })), fieldsToDisplay.length === 0 && (_jsx(Box, Object.assign({ padding: "300" }, { children: _jsxs(Text.Paragraph, Object.assign({ variant: "1" }, { children: ["No filters matching \"", search, "\""] })) })))] }))) }) }, { children: children })));
};
